import * as React from 'react'
import {
  Accordion,
  Box,
  OutlinedButton,
  Grid,
  FormHelperText,
  Autocomplete,
} from 'cs-kevlar-react'
import { FormikProps } from 'formik'
import { FormikTextField } from 'components'
import * as Yup from 'yup'
import {
  makeStyles,
  createStyles,
  useTheme,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { UserPermissions } from '../UserPermissions'
import { Capabilities } from './Capabilities'
import clsx from 'clsx'
import { EditableUser } from '../../contexts/base'
import { Search } from 'cs-kevlar-react/lib/es/icons'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import { GlobalConfiguration } from './GlobalConfiguration'

interface EditUserFormProps {
  formikProps: FormikProps<EditableUser>
  canViewCapabilities: boolean
  canViewPermissions: boolean
  canViewGlobalConfiguration: boolean
  allowSearch: boolean
  onSearch?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridItem: {
      width: '100%',
    },
    disabledMultiline: {
      '& .MuiInput-underline.Mui-disabled:before': {
        borderBottomStyle: 'solid',
      },
    },
    nameRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    name: {
      flexGrow: 1,
    },
    searchButton: {
      marginLeft: theme.spacing(8),
    },
    removePadding: {
      '& .MuiAutocomplete-inputRoot': {
        paddingRight: '0',
      },
    },
    accordion: {
      '& .MuiAccordionDetails-root': {
        flexDirection: 'column',
      },
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      width: '100%',
    },
  }),
)

export const EditUserFormSchema = Yup.object().shape({
  fullName: Yup.string().required('The Name field is required.').nullable(),
  name: Yup.string().required('The Username field is required.').nullable(),
  phone: Yup.string().required('The Phone field is required.').nullable(),
  email: Yup.string()
    .required('The Email field is required.')
    .email()
    .nullable(),
})

const readOnlyInputProps = {
  readOnly: true,
}

export const EditUserForm: React.FunctionComponent<EditUserFormProps> = ({
  formikProps,
  canViewCapabilities,
  canViewPermissions,
  canViewGlobalConfiguration,
  allowSearch,
  onSearch,
}) => {
  const classes = useStyles({})
  const theme = useTheme<Theme>()

  const phoneOptions: string[] = formikProps.values.externalPhoneList
    ? formikProps.values.externalPhoneList
    : []
  const emailOptions: string[] = formikProps.values.externalEmailList
    ? formikProps.values.externalEmailList
    : []

  return (
    <React.Fragment>
      <Box className={classes.formFieldContainer}>
        <Accordion
          title="User Details"
          initiallyExpanded
          id="EditUser::Details"
          className={classes.accordion}
        >
          <Grid
            container
            spacing={Spacing}
            direction="column"
            alignItems="flex-start"
            justifyContent="center"
          >
            <Grid item className={classes.gridItem}>
              <Box className={classes.nameRow}>
                <Box className={classes.name}>
                  <FormikTextField
                    formikProps={formikProps}
                    fieldName="fullName"
                    label="Name"
                    fullWidth
                    inputProps={readOnlyInputProps}
                  />
                </Box>

                {allowSearch && (
                  <OutlinedButton
                    onClick={onSearch}
                    className={classes.searchButton}
                  >
                    <Search />
                  </OutlinedButton>
                )}
              </Box>
            </Grid>
            <Grid item className={classes.gridItem}>
              <FormikTextField
                formikProps={formikProps}
                fieldName="name"
                label="Username"
                fullWidth
                inputProps={readOnlyInputProps}
              />
            </Grid>
            <Grid item className={classes.gridItem}>
              <Autocomplete
                id="phone"
                className={classes.removePadding}
                freeSolo={true}
                disableClearable={true}
                options={phoneOptions}
                defaultValue={formikProps.values.phone ?? phoneOptions[0] ?? ''}
                onChange={(event, newValue) => {
                  formikProps.setFieldValue('phone', newValue)
                }}
                onBlur={formikProps.handleBlur}
                renderInput={(params) => (
                  <FormikTextField
                    {...params}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
                    }}
                    label="Phone"
                    formikProps={formikProps}
                    fieldName="phone"
                    fullWidth
                    required
                  />
                )}
              />
            </Grid>
            <Grid item className={classes.gridItem}>
              <Autocomplete
                id="email"
                className={classes.removePadding}
                freeSolo={true}
                disableClearable={true}
                options={emailOptions}
                defaultValue={formikProps.values.email ?? emailOptions[0] ?? ''}
                onChange={(event, newValue) => {
                  formikProps.setFieldValue('email', newValue)
                }}
                onBlur={formikProps.handleBlur}
                renderInput={(params) => (
                  <FormikTextField
                    {...params}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
                    }}
                    label="Email"
                    formikProps={formikProps}
                    fieldName="email"
                    fullWidth
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        </Accordion>

        {(canViewPermissions ||
          canViewCapabilities ||
          canViewGlobalConfiguration) && (
          <Accordion
            title="Additional Information"
            initiallyExpanded
            id="EditUser::AdditionalInformation"
            className={classes.accordion}
          >
            <Grid
              container
              spacing={Spacing}
              direction="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              {canViewCapabilities && (
                <Grid
                  item
                  className={clsx(classes.gridItem, classes.disabledMultiline)}
                >
                  <Capabilities
                    capabilities={formikProps.values.capabilities}
                  />
                  <FormHelperText></FormHelperText>
                </Grid>
              )}
              {canViewPermissions && (
                <Grid
                  item
                  className={clsx(classes.gridItem, classes.disabledMultiline)}
                >
                  <UserPermissions
                    permissions={formikProps.values.permissions}
                  />
                  <FormHelperText></FormHelperText>
                </Grid>
              )}
              {canViewGlobalConfiguration && (
                <Grid
                  item
                  className={clsx(classes.gridItem, classes.disabledMultiline)}
                >
                  <GlobalConfiguration
                    configuration={formikProps.values.globalConfiguration}
                    title="Global Configuration"
                  />
                  <FormHelperText></FormHelperText>
                </Grid>
              )}
              {canViewGlobalConfiguration && (
                <Grid
                  item
                  className={clsx(classes.gridItem, classes.disabledMultiline)}
                >
                  <GlobalConfiguration
                    configuration={
                      formikProps.values.userAgencyGlobalConfiguration
                    }
                    title="User's Agency Specific Global Configuration"
                  />
                  <FormHelperText></FormHelperText>
                </Grid>
              )}
            </Grid>
          </Accordion>
        )}
      </Box>
    </React.Fragment>
  );
}
